<template>
  <div>
    <slot/>
  </div>
</template>

<script setup>
import Highcharts from "@/highcharts"
import { SchoolType } from "@/models/SchoolType"
import { currency } from "@/support"
import { onMounted } from "vue"

const props = defineProps({
  debt: Array,
  earnings: Array,
  ratios: Array,
  year1: Number,
  year2: Number,
  series: Array,
})

onMounted(() => {
  const { debt, earnings, ratios, year1, year2, series: data } = props

  const series = data.map((s) => {
    return {
      name: new SchoolType(s.name).label(),
      data: s.data,
      marker: {
        symbol: "circle",
      },
      color: s.color,
    }
  })

  new Highcharts.chart("debtVincome", {
    chart: {
      type: "scatter",
      zoomType: "xy",
      height: 500,
    },
    title: {
      text: "Median Earnings vs. Median Amount Borrowed",
    },
    subtitle: {
      text: `${year1} and ${year2} Graduates (combined)`,
    },
    xAxis: {
      title: {
        enabled: true,
        text: "Median Borrowed",
      },
      startOnTick: true,
      endOnTick: true,
      tickInterval: 25000,
      showLastLabel: true,
      max: 200000,
      min: 0,
      labels: {
        formatter() {
          return `$${Math.round(this.value / 1000)}k`
        },
      },
    },
    yAxis: {
      title: {
        text: "Median Earnings",
      }, min: 0, max: 200000,
      labels: {
        formatter() {
          return `$${Math.round(this.value / 1000)}k`
        },
      },
      endOnTick: false,
    },
    legend: {
      enabled: true,
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 5,
          states: {
            hover: {
              enabled: true,
              lineColor: "rgb(100,100,100)",
            },
          },
        },
        states: {
          hover: {
            marker: {
              enabled: false,
            },
          },
        },
      },
    },
    tooltip: {
      useHTML: true,
      formatter() {
        return `<b>${this.point.name}</b><br>Median Borrowed:${currency(this.x)}<br>Median Earnings:${currency(this.y)}<br>Debt to Income Ratio: ${Math.round((this.x / this.y) * 100) / 100}`
      },
    },
    series,
  })

  new Highcharts.chart("ratioOnly", {
    chart: { type: "column", height: 300 },
    title: { text: "Debt-to-Income Ratio" },
    legend: { enabled: false },
    xAxis: {
      categories: ratios.map(i => i.name),
      crosshair: true,
    },
    yAxis: {
      min: 0,
      title: { text: "# of Schools" },
    },
    tooltip: {
      headerFormat: '<span style="color:{series.color};padding:0">{series.name}, {point.key}</span><br>',
      pointFormat: "<b>{point.y} Schools</b>",
      footerFormat: "</table>",
      shared: true,
      useHTML: true,
    },
    plotOptions: { column: { pointPadding: 0, borderWidth: 0, groupPadding: 0, shadow: false }, series: { color: "#742396" } },
    series: [{
      name: "Debt-to-Income Ratio",
      data: ratios.map(i => i.count),
    }],
  })

  new Highcharts.chart("earningsOnly", {
    chart: { type: "column", height: 300 },
    title: { text: "Median Earnings ($)" },
    legend: { enabled: false },
    xAxis: {
      categories: earnings.map(i => i.name),
      crosshair: true,
    },
    yAxis: {
      min: 0,
      title: { text: "# of Schools" },
    },
    tooltip: {
      headerFormat: '<span style="color:{series.color};padding:0">{series.name}, {point.key}</span><br>',
      pointFormat: "<b>{point.y} Schools</b>",
      footerFormat: "</table>",
      shared: true,
      useHTML: true,
    },
    plotOptions: { column: { pointPadding: 0, borderWidth: 0, groupPadding: 0, shadow: false }, series: { color: "#742396" } },
    series: [{
      name: "Median Earnings",
      data: earnings.map(i => i.count),
    }],
  })

  new Highcharts.chart("debtOnly", {
    chart: { type: "column", height: 300 },
    title: { text: "Median Borrowed ($)" },
    legend: { enabled: false },
    xAxis: {
      categories: debt.map(i => i.name),
      crosshair: true,
    },
    yAxis: {
      min: 0,
      title: { text: "# of Schools" },
    },
    tooltip: {
      headerFormat: '<span style="color:{series.color};padding:0">{series.name}, {point.key}</span><br>',
      pointFormat: "<b>{point.y} Schools</b>",
      footerFormat: "</table>",
      shared: true,
      useHTML: true,
    },
    plotOptions: { column: { pointPadding: 0, borderWidth: 0, groupPadding: 0, shadow: false }, series: { color: "#742396" } },
    series: [{
      name: "Median Borrowed",
      data: debt.map(i => i.count),
    }],
  })
})
</script>
